import type { KeyboardActionId } from "~/keyboard/actionCatalog";
import type { ShortcutBindingConflict } from "~/keyboard/shortcutSettingsModel";

import { Box, Button, Paper, Switch } from "@mui/material";
import { useMemo, useState } from "react";

import SettingRow from "~/components/settings/SettingRow";
import SettingsSection from "~/components/settings/SettingsSection";
import { BodyText, CaptionText, TitleText } from "~/components/Typographies";
import SearchTextField from "~/components/textfield/SearchTextField";
import useHintBadgeMode from "~/hooks/useHintBadgeMode";
import useKeybindingOverrides from "~/hooks/useKeybindingOverrides";
import keyboardActions, { effectiveBindingsForAction, getKeyboardAction } from "~/keyboard/actionCatalog";
import { detectIsMac } from "~/keyboard/bindings";
import {
	buildReadOnlyShortcutRows,
	buildShortcutSettingRows,
	filterShortcutRows,
	findBindingConflicts,
	groupShortcutRowsByCategory
} from "~/keyboard/shortcutSettingsModel";
import BuiltInShortcutsSection from "~settings/shortcuts/components/BuiltInShortcutsSection";
import ShortcutRowControl from "~settings/shortcuts/components/ShortcutRowControl";

interface ConflictPrompt {
	actionId: KeyboardActionId;
	binding: string;
	conflicts: ShortcutBindingConflict[];
	previousBinding: string | null;
}

interface ShortcutsProps {
	hosted?: boolean;
}

const Shortcuts = ({ hosted = false }: ShortcutsProps) => {
	const { disableAction, overrides, resetAction, resetAll, setBinding } = useKeybindingOverrides();
	const { enabled: hintBadgesEnabled, setEnabled: setHintBadgesEnabled } = useHintBadgeMode();
	const [search, setSearch] = useState("");
	const [recordingActionId, setRecordingActionId] = useState<KeyboardActionId | null>(null);
	const [conflict, setConflict] = useState<ConflictPrompt | null>(null);
	const isMac = useMemo(detectIsMac, []);

	const groups = useMemo(() => {
		const rows = buildShortcutSettingRows(keyboardActions, overrides);
		return groupShortcutRowsByCategory(filterShortcutRows(rows, search));
	}, [overrides, search]);

	const appBuiltIns = useMemo(() => buildReadOnlyShortcutRows(keyboardActions), []);
	const hasOverrides = Object.keys(overrides).length > 0;

	const startRecording = (actionId: KeyboardActionId) => {
		setConflict(null);
		setRecordingActionId(actionId);
	};

	const commitBinding = (actionId: KeyboardActionId, binding: string) => {
		const action = getKeyboardAction(actionId);
		const previousBinding = action ? (effectiveBindingsForAction(action, overrides)[0] ?? null) : null;
		const conflicts = findBindingConflicts(actionId, binding, keyboardActions, overrides);
		setBinding(actionId, binding);
		setRecordingActionId(null);
		setConflict(conflicts.length > 0 ? { actionId, binding, conflicts, previousBinding } : null);
	};

	const clearConflicting = () => {
		if (!conflict) return;
		for (const collision of conflict.conflicts) disableAction(collision.actionId);
		setConflict(null);
	};

	const swapConflicting = () => {
		if (!conflict) return;
		for (const collision of conflict.conflicts) {
			if (conflict.previousBinding) setBinding(collision.actionId, conflict.previousBinding);
			else disableAction(collision.actionId);
		}
		setConflict(null);
	};

	const resetEverything = () => {
		resetAll();
		setRecordingActionId(null);
		setConflict(null);
	};

	return (
		<Paper elevation={0} sx={{ p: 3 }}>
			<Box sx={{ alignItems: "flex-start", display: "flex", gap: 2, justifyContent: "space-between", mb: 3 }}>
				<Box>
					{!hosted && <TitleText bold>Keyboard shortcuts</TitleText>}
					<BodyText color="text.secondary">
						Customise shortcuts for this device. Changes apply everywhere immediately.
					</BodyText>
				</Box>
				<Button disabled={!hasOverrides} onClick={resetEverything} size="small" variant="outlined">
					Reset all to defaults
				</Button>
			</Box>

			<SettingsSection title="Hints">
				<SettingRow
					control={
						<Switch
							checked={hintBadgesEnabled}
							onChange={(event) => setHintBadgesEnabled(event.target.checked)}
							slotProps={{ input: { "aria-label": "Show keyboard hints" } }}
						/>
					}
					description="Overlay keycap badges on shortcut-bound controls. Toggle any time with the shortcut below."
					label="Show keyboard hints"
				/>
			</SettingsSection>

			<Box sx={{ mb: 4 }}>
				<SearchTextField onChange={setSearch} placeholder="Search shortcuts" value={search} />
			</Box>

			{groups.length === 0 ? (
				<CaptionText color="text.secondary">No shortcuts match your search.</CaptionText>
			) : (
				groups.map((group) => (
					<SettingsSection key={group.category} title={group.category}>
						{group.rows.map((row) => (
							<SettingRow
								control={
									<ShortcutRowControl
										conflicts={conflict?.actionId === row.actionId ? conflict.conflicts : []}
										isMac={isMac}
										isRecording={recordingActionId === row.actionId}
										onClearConflicts={clearConflicting}
										onCommit={(binding) => commitBinding(row.actionId, binding)}
										onDisable={() => disableAction(row.actionId)}
										onRecord={() => startRecording(row.actionId)}
										onReset={() => resetAction(row.actionId)}
										onStopRecording={() => setRecordingActionId(null)}
										onSwap={swapConflicting}
										row={row}
									/>
								}
								key={row.actionId}
								label={row.label}
							/>
						))}
					</SettingsSection>
				))
			)}

			<BuiltInShortcutsSection appShortcuts={appBuiltIns} isMac={isMac} />
		</Paper>
	);
};

export default Shortcuts;
